import { createSelector } from '@reduxjs/toolkit';
// Types
import StaplePageActions from '@root/types/StaplePageActions';
// Models
import { IPage, IPageDuplicate, IPageWithAction } from '@root/models/Page';
import { IEpisode } from '@root/models/Episode';
// Selectors
import { selectDuplicatesList } from 'app/store/Cases/Cases.selectors';
// Services
import { generatePageId } from './page.service';
// Utilities
import { sortByStapleOrder, sortByWorkspaceOrder } from '@root/utilities/sort';

import { selectDocumentsDates } from 'app/store/DMSDocuments/DMSDocuments.selectors';
import Reducers from 'app/types/Reducers';
import { sortByDateTime } from 'app/utilities/SortBy';


import { selectEpisodesEntities, selectEpisodesAll } from 'app/store/Episodes/Episodes.selectors';

export const selectPageIds = (state:any) => state.page.pageIds;
export const selectPageEntities = (state:any) => state.page.pageEntities;
export const selectAllPages = (state:any) => state.page.pages;
export const selectPage = (state:any) => state.page.page;
export const selectFilter = (state:any) => state.page.filter;
export const selectIsLoading = (state:any) => state.page.isLoading;
export const selectStatus = (state:any) => state.page.status;
export const selectError = (state:any) => state.page.error;

export const selectPageStaples = (state:any) => state.page.pageStaples;
// Compare
export const selectComparePagesIds = (state:any) => state.page.comparePagesIds;
// Staple
export const selectStapleMode = (state:any) => state.page.stapleMode;
export const selectStapleId = (state:any) => state.page.stapleId;
export const selectStaplePagesToUpdate = (state:any) => state.page.staplePagesToUpdate;
export const selectDisabledPagesIds = (state:any) => state.page.disabledPagesIds;

interface IPageEntities {
  [key:number]: Record<number, IPage>
};

export const selectPageDates = createSelector(
  [
    selectDocumentsDates,
    (_:any, props:{ documentId:number, pageNum:number, isFilesPage:boolean }) => props
  ],
  (batchDates, { documentId, pageNum, isFilesPage }) => {
    if ( !batchDates ) return [];
    let dates:any = batchDates[`${documentId}:${pageNum}`];
    if ( !dates || dates.length === 0 ){
      dates = [];
    } else {
      dates = [...dates].map(d => ({ date: d, fromPrevPage: false }))
    }
    return dates;
  }
);

export const selectPageEntity = createSelector(
  [
    selectPageEntities,
    (_:any, props:{ documentId:number, pageNum:number }) => props
  ],
  (entities:IPageEntities, { documentId, pageNum }) => {
    const document = entities[documentId];
    if (!document) return undefined; // Might happen if new document is created by another user. This isn't handled on background at runtime.
    return document[pageNum];
  }
);

export const selectPrimaryPage = createSelector(
  [
    selectPageEntities,
    (_:any, props:{ documentId:number, pageNum:number }) => props
  ],
  (entities:IPageEntities, { documentId, pageNum }) => {
    const documentPageMap = entities[documentId];
    if ( !documentPageMap ) return null;
    const page = documentPageMap[pageNum]
    if ( !page.staple || page.staple.order === 1 ) return page;
    return Object.values(documentPageMap).find((p) => p.staple?.id === page.staple?.id && p.staple?.order === 1) || null;
  }
);

export const selectStapleById = createSelector(
  [
    selectAllPages,
    (_:any, props:{ stapleId:string|null }) => props
  ],
  (pages:Array<IPage> | null, { stapleId }) => {
    if ( !pages || !stapleId ) return null;
    const id = stapleId.split(':')[0];
    return pages.filter((page:IPage) => {
      if ( !page.staple ) return false;
      return (page.staple.id === stapleId || page.staple.id.includes(id));
    }).sort(sortByStapleOrder)
  }
);

export const selectIsPageDisabled = createSelector(
  [
    selectDisabledPagesIds,
    (_:any, props:{ documentId:number, pageNum:number }) => props
  ],
  (disabledPagesIds:Array<string>, { documentId, pageNum }) => disabledPagesIds.includes(generatePageId(documentId, pageNum))
);

export const selectIsPageInStapleUpdate = createSelector(
  [
    selectStaplePagesToUpdate,
    (_:any, props:{ documentId:number, pageNum:number }) => props
  ],
  (staplePagesToUpdate:Array<IPageWithAction>, { documentId, pageNum }) => {
    const foundedPage = staplePagesToUpdate.find((page:IPageWithAction) => {
      return page.documentId === documentId && page.pageNum === pageNum && page.action !== StaplePageActions.Removed
    });
    return Boolean(foundedPage);
  }
);

export const selectIsPageInCompare = createSelector(
  [
    selectComparePagesIds,
    (_:any, props:{ documentId:number, pageNum:number, stapleId:string|null }) => props
  ],
  (comparePagesIds:Array<string>, { documentId, pageNum, stapleId }) => {
    return comparePagesIds.includes(generatePageId(documentId, pageNum, stapleId))
  }
);

export const selectPagesDatesMap = createSelector(
  [
    selectAllPages,
    selectEpisodesEntities,
  ],
  (pages:Array<IPage> | null, episodesEntities:any) => {
    if ( !pages ) return {};
    return pages.reduce((acc:{ [key:string]:string }, cur:IPage) => {
      if ( cur.parentEpisodeId ){
        const episode = episodesEntities[cur.parentEpisodeId];
        if ( episode && episode.date ){
          acc[generatePageId(cur.documentId, cur.pageNum)] = episode.date;
        }
      }
      return acc;
    }, {})
  }
)

export const selectPrevStaplePage = createSelector(
  [
    selectStapleById,
    (_:any, props:{ pageNum:number }) => props
  ],
  (staple:Array<IPage>|null, { pageNum }) => {
    if ( !staple ) return null;
    const pageIndex = staple.findIndex((page:IPage) => page.pageNum === pageNum);
    return staple[pageIndex - 1];
  }
);

export const selectNextStaplePage = createSelector(
  [
    selectStapleById,
    (_:any, props:{ pageNum:number }) => props
  ],
  (staple:Array<IPage>|null, { pageNum }) => {
    if ( !staple ) return null;
    const pageIndex = staple.findIndex((page:IPage) => page.pageNum === pageNum);
    return staple[pageIndex + 1];
  }
);

// Workspace
export const selectPagesInWorkspaceIds = createSelector(
  [ selectAllPages ],
  (pages:Array<IPage> | null) => {
    if ( !pages ) return [];
    return pages
      .filter((page:IPage) => !page.deleted && page.workspaceOrder)
      .map((page:IPage) => generatePageId(page.documentId, page.pageNum))
    ;
  }
);

export const selectSortedDocumentsPages = createSelector(
  [
    selectPageEntities,
    (_:any, props:{ documentId:number }) => props.documentId
  ],
  ( pageEntities:IPageEntities, documentId ) => {
    if ( Object.keys(pageEntities).length === 0 ) return null;
    const documentPages = pageEntities[documentId];
    if ( !documentPages ) return null;
    const sortedDocumentsPages = Object.values(documentPages)
      .map(generateMainStaplePageNum)
      .sort(sortByMainStaplePageNum)
    ;
    return sortedDocumentsPages;
    // ToDO: Hz why do we need this ? Probably for navigation in `CardPageDialog`
    // const documentIds = [...batches]
    //   .sort((a, b) => sortByCreationDate(a, b, 'asc'))
    //   .reduce((acc:any, cur:any) => {
    //     return [...acc, ...cur.documentIds]
    //   }, []);
    // let sortedDocumentsPages:any[] = [];
    // for ( let documentId of documentIds ){
    //   const documentPages = pages
    //     .filter((page:any) => page.documentId === documentId)
    //     .map(generateMainStaplePageNum)
    //     .sort(sortByMainStaplePageNum)
    //   ;
    //   sortedDocumentsPages = [...sortedDocumentsPages, ...documentPages]
    // }
    // return sortedDocumentsPages;
  }
)

export const selectFilteredDocumentPages = createSelector(
  [
    selectSortedDocumentsPages,
    (state) => selectDuplicatesList(state),
    (state) => selectPagesInWorkspaceIds(state),
    (state) => selectFilter(state),
    selectEpisodesAll
  ],
  ( pages:any, duplicates, workspacePagesIds:any, filter:any, episodes:any ) => {
    if ( !pages || !duplicates || !episodes ) return null;

    const { tags, colors, types, labels, authors, haveDuplicateInWorkspace, pagesInWorkspace } = filter;

    let filteredPages = pages
      .filter((page:IPage) => {
        const pageTag = page.tag || null;
        const pageColors = page.colors?.length ? page.colors : ['noColor'];
        const hasTags = tags.length === 0 || tags.includes(pageTag);
        const hasColors = colors.length === 0 || colors.some((color:string) => pageColors.includes(color));
        return hasTags && hasColors;
      })
    ;

    if ( types.length || labels.length || authors.length ){
      filteredPages = filteredPages.filter((page:IPage) => {
        const matchingEpisodes = episodes.filter(
          (ep: IEpisode) => ep.documentId === page.documentId && ep.pageNum === page.pageNum
        );

        // Check if any of the matching episodes meet the filter criteria
        const hasMatchingEpisode = matchingEpisodes.some((episode:IEpisode) => {
          const hasTypes = types.length === 0 || (episode.type && types.includes(episode.type));
          const hasLabels = labels.length === 0 || (episode.labels?.some((label: string) => labels.includes(label)));
          const hasAuthors = authors.length === 0 || (episode.author?.name && authors.includes(episode.author.name));
          return hasTypes && hasLabels && hasAuthors;
        });

        return hasMatchingEpisode;
      })
    }

    if ( pagesInWorkspace ){
      filteredPages = filteredPages.filter((page:IPage) => {
        const pageInWorkspace = page.inWorkspace || false;
        return pagesInWorkspace === 'all' ? true : toBoolean(pagesInWorkspace) === pageInWorkspace
      })
    }

    if ( haveDuplicateInWorkspace ){
      filteredPages = filteredPages.filter((page:IPage) => {
        if ( !haveDuplicateInWorkspace ) return true;
        if ( !page.inWorkspace ) return false;

        let selectedCluster = null;

        for ( let cluster of Object.values(duplicates) ){
          for ( let p of cluster as any ){
            if ( p.documentId === page.documentId && p.pageNum === page.pageNum ){
              selectedCluster = cluster;
              break;
            }
          }
          if ( selectedCluster ) break;
        }

        if ( !selectedCluster ) return false;

        let hasPageDuplicate = false;

        for ( let p of selectedCluster as any ){
          if ( p.documentId === page.documentId && p.pageNum === page.pageNum ) continue;
          if ( workspacePagesIds.includes(`${p.documentId}:${p.pageNum}`) ){
            hasPageDuplicate = true;
            break;
          }
        }

        return hasPageDuplicate;
      })
    }

    return filteredPages
  }
)

// 
function toBoolean(value: 'true' | 'false'): boolean {
  return value === 'true';
}

export const selectPagesInWorkspace = createSelector(
  [
    selectAllPages,
    selectPageEntities,
    selectEpisodesAll,
    selectFilter,
    (state:any) => selectDuplicatesList(state),
    (state:any) => state[Reducers.DMSBatches].entities,
  ],
  (pages:Array<IPage> | null, pageEntities:any, episodes:any, filter:any, duplicates:any, batchesEntities:any) => {
    if ( !pages || !episodes ) return null;

    const { tags, colors, types, labels, authors, haveDuplicateInWorkspace, additionalBatchesOnly } = filter;

    const workspacePages = pages
      .filter((page:IPage) => page.inWorkspace && !page.deleted)
      .sort(sortByWorkspaceOrder);

    let filteredPages = workspacePages
      .filter((page:IPage) => {
        const pageTag = page.tag || null;
        const pageColors = page.colors?.length ? page.colors : ['noColor'];
        const hasTags = tags.length === 0 || tags.includes(pageTag);
        const hasColors = colors.length === 0 || colors.some((color:string) => pageColors.includes(color));
        return hasTags && hasColors;
      })
    ;

    if ( types.length || labels.length || authors.length ){
      const matchedStapleIds = new Set<string>();
      const matchedPageKeys = new Set<string>();

      filteredPages.forEach((page:IPage) => {
        const matchingEpisodes = episodes.filter(
          (ep: IEpisode) => ep.documentId === page.documentId && ep.pageNum === page.pageNum
        );

        // Check if any of the matching episodes meet the filter criteria
        const hasMatchingEpisode = matchingEpisodes.some((episode:IEpisode) => {
          const hasTypes = types.length === 0 || (episode.type && types.includes(episode.type));
          const hasLabels = labels.length === 0 || (episode.labels?.some((label: string) => labels.includes(label)));
          const hasAuthors = authors.length === 0 || (episode.author?.name && authors.includes(episode.author.name));
          return hasTypes && hasLabels && hasAuthors;
        });

        if ( hasMatchingEpisode ) {
          if (page.staple?.id) {
            matchedStapleIds.add(page.staple.id);
          } else {
            matchedPageKeys.add(`${page.documentId}_${page.pageNum}`);
          }
        }
      })

      filteredPages = filteredPages.filter((page:IPage) => {
        if ( !page.staple ) return matchedPageKeys.has(`${page.documentId}_${page.pageNum}`);
        return matchedStapleIds.has(page.staple.id);
      })
    }

    if ( haveDuplicateInWorkspace ){
      const pageKey = (page: IPage) => `${page.documentId}_${page.pageNum}`;
      const filteredPageMap = new Map(filteredPages.map((p) => [pageKey(p), p]));

      const resultSet = new Set<string>();
      const stapleIdsToInclude = new Set<string>();

      Object.values(duplicates).forEach((cluster:any) => {
        const filteredClusterPages = cluster.filter((p:any) => filteredPageMap.has(pageKey(p)));
        if ( filteredClusterPages.length > 1 ) {
          filteredClusterPages.forEach((p:any) => {
            const pageEntity = pageEntities[p.documentId][p.pageNum];
            if ( pageEntity.staple?.order > 1 && pageEntity.staple?.id ){
              stapleIdsToInclude.add(pageEntity.staple.id);
            }
            resultSet.add(pageKey(p));
          });
        }
      });

      filteredPages = filteredPages.filter((p) => {
        const keyMatch = resultSet.has(pageKey(p));
        const isPrimaryFromMatchedStaple = p.staple?.order === 1 && p.staple?.id && stapleIdsToInclude.has(p.staple.id);
        return keyMatch || isPrimaryFromMatchedStaple;
      });
    }

    if ( additionalBatchesOnly ) {
      const documentIds = Object.values(batchesEntities).filter((batch:any) => batch.additional)
        .reduce((acc:number[], cur:any) => {
          if ( cur.documents && cur.documents.length ){
            for ( let doc of cur.documents ){
              acc.push(doc.id);
            }
          }
          return acc;
        }, []);

      filteredPages = filteredPages.filter((page:IPage) => documentIds.includes(page.documentId))
    }

    return filteredPages;
  }
);

export const selectPagesInWorkspaceWithoutSecondary = createSelector(
  [selectPagesInWorkspace],
  (pages:Array<IPage> | null) => {
    if ( !pages ) return null;
    return pages.filter((page:IPage) => {
      if ( page.staple && page.staple.order > 1 ) return false;
      return true;
    })
  }
);

export const selectTotalPagesInWorkspace = createSelector(
  [selectAllPages, selectPagesInWorkspace],
  (allPages:IPage[] | null, pagesInWorkspace:Array<IPage> | null) => {
    if ( !allPages || !pagesInWorkspace ) return null;

    let filteredPages:any = [];

    pagesInWorkspace.forEach((page:IPage) => {
      if ( !page.staple ){
        filteredPages = [...filteredPages, page];
      } else {
        if ( page.staple.order === 1 ){
          const staplePages:Array<IPage> = allPages.filter((p:IPage) => p.staple && p.staple.id === page?.staple?.id).sort(sortByStapleOrder);
          filteredPages = [...filteredPages, ...staplePages];
        }
      }
    });

    return {
      totalPagesInWorkspace: filteredPages.length,
      totalMainPagesInWorkspace: filteredPages.filter((page:IPage) => !page.staple || page.staple.order === 1).length
    }
  }
);

export const selectPagesInWorkspaceByPreEvent = createSelector(
  [
    (state:any) => selectPagesInWorkspaceWithoutSecondary(state),
    (state:any) => selectEpisodesEntities(state),
    (_:any, props:{ preEvent:boolean }) => props
  ],
  (pages:Array<IPage> | null, episodesEntities:any, { preEvent }) => {
    if ( !pages || !episodesEntities ) return null;

    return pages
      .filter((page:IPage) => {
        if ( page.parentEpisodeId ){
          const parentEpisode:IEpisode = episodesEntities[page.parentEpisodeId];

          if ( !parentEpisode ) return false;

          return preEvent ? parentEpisode.preEvent : !parentEpisode.preEvent;
        }
        return preEvent ? false : true;
      })
  }
);

export const selectEpisodesInWorkspace = createSelector(
  [
    (state:any) => selectAllPages(state),
    (state:any) => selectFilter(state),
    (state:any) => selectEpisodesAll(state),
    (state) => state[Reducers.DMSBatches].entities
  ],
  (pages:any, { tags = [], colors = [], types = [], labels = [], authors = [], additionalBatchesOnly }, episodes:any, batcheEntities) => {
    if ( !pages || !episodes ) return null;

    const pagesIds = pages
      .filter((page:any) => page.workspaceOrder && !page.deleted)
      .sort(sortByWorkspaceOrder)
      .filter((page:any) => {
        const pageTag = page.tag || null;
        const pageColors = page.colors && page.colors.length ? page.colors : ['noColor'];
        const hasTags = tags.length === 0 ? true : tags.includes(pageTag);
        const hasColors = colors.length === 0 ? true : colors.some((color:string) => pageColors.includes(color));

        return hasTags && hasColors
      })
      .map((page:any) => `${page.documentId}:${page.pageNum}`);

    const nextEpisodes = episodes
      .filter((episode:any) => pagesIds.includes(`${episode.documentId}:${episode.pageNum}`))
      .filter((episode:any) => {

        let hasTypes = types.length === 0;
        if ( !hasTypes ){
          if ( episode.type ){
            hasTypes = types.includes(episode.type);
          }
        }
        let hasLabels = labels.length === 0;
        if ( !hasLabels ){
          if ( episode.labels && episode.labels.length ){
            for ( let label of episode.labels ){
              if ( labels.includes(label) ){
                hasLabels = true;
                break;
              }
            }
          }
        }
        let hasAuthors = authors.length === 0;
        if ( !hasAuthors ){
          if ( episode.author && episode.author.name ){
            hasAuthors = authors.includes(episode.author.name);
          }
        }
        return hasTypes && hasLabels && hasAuthors;
      });

    if ( !additionalBatchesOnly ) return nextEpisodes;

    const documentIds = Object.values(batcheEntities).filter((batch:any) => batch.additional)
      .reduce((acc:number[], cur:any) => {
        if ( cur.documents && cur.documents.length ){
          for ( let doc of cur.documents ){
            acc.push(doc.id);
          }
        }
        return acc;
      }, []);

    return nextEpisodes.filter((episode:any) => documentIds.includes(episode.documentId));
  }
);

export const selectEpisodesInWorkspaceByPreEvent = createSelector(
  [
    (state:any) => selectEpisodesInWorkspace(state),
    (_:any, props:{ preEvent:boolean }) => props
  ],
  (episodes:any, { preEvent }) => {
    if ( !episodes ) return null;
    return episodes.filter((episode:any) => {
      if ( !preEvent ) return !episode.preEvent;
      return episode.preEvent === preEvent;
    }).sort((a:any, b:any) => sortByDateTime(a, b));
  }
)



export const selectPrevWorkspacePage = createSelector(
  [
    (state:any) => selectPagesInWorkspaceWithoutSecondary(state),
    (state:any) => selectEpisodesEntities(state),
    (_:any, props:{ documentId:number, pageNum:number }) => props
  ],
  (pages:Array<IPage> | null, episodeEntities:any, { documentId, pageNum }) => {
    if ( !pages ) return null;
    const page:IPage | undefined = pages.find((page:IPage) => page.documentId === documentId && page.pageNum === pageNum);
    
    if ( page ){
      const pageIndex = pages.indexOf(page);
      const prevPage = pages[pageIndex - 1];

      const pageEpisode = page.parentEpisodeId && episodeEntities ? episodeEntities[page.parentEpisodeId] : null;
      const prevPageEpisode = prevPage && prevPage.parentEpisodeId && episodeEntities ? episodeEntities[prevPage.parentEpisodeId] : null;

      if (
        (pageEpisode?.date &&
          prevPageEpisode?.date &&
          pageEpisode.date === prevPageEpisode.date &&
          !!pageEpisode.preEvent === !!prevPageEpisode.preEvent) ||
        (!pageEpisode?.date && !prevPageEpisode?.date)
      ) {
        return prevPage;
      }
    }
    return null;
  }
);

export const selectNextWorkspacePage = createSelector(
  [
    (state:any) => selectPagesInWorkspaceWithoutSecondary(state),
    (state:any) => selectEpisodesEntities(state),
    (_:any, props:{ documentId:number, pageNum:number }) => props
  ],
  (pages:Array<IPage> | null, episodeEntities, { documentId, pageNum }) => {
    if ( !pages ) return null;
    const page:IPage | undefined = pages.find((page:IPage) => page.documentId === documentId && page.pageNum === pageNum);
    if ( page ){
      const pageIndex = pages.indexOf(page);
      const nextPage = pages[pageIndex + 1];

      const pageEpisode = page.parentEpisodeId && episodeEntities ? episodeEntities[page.parentEpisodeId] : null;
      const nextPageEpisode = nextPage && nextPage.parentEpisodeId && episodeEntities ? episodeEntities[nextPage.parentEpisodeId] : null;

      if (
        (pageEpisode?.date &&
          nextPageEpisode?.date &&
          pageEpisode.date === nextPageEpisode.date &&
          !!pageEpisode.preEvent === !!nextPageEpisode.preEvent) ||
        (!pageEpisode?.date && !nextPageEpisode?.date)
      ) {
        return nextPage;
      }
    }
    return null;
  }
);

// Duplicates
export const selectPageDuplicateInWorkspace = createSelector(
  [
    (state:any) => selectPagesInWorkspaceIds(state),
    (state:any) => selectDuplicatesList(state),
    (_:any, props:{ documentId:number, pageNum:number }) => props
  ],
  (pagesIds:Array<string> | null, duplicates, { documentId, pageNum }) => {
    if ( !pagesIds || !duplicates ) return null;

    let selectedCluster:IPageDuplicate[] | null = null;

    for ( let cluster of Object.values(duplicates) ){
      for ( let page of cluster ){
        if ( page.documentId === documentId && page.pageNum === pageNum ){
          selectedCluster = cluster;
          break;
        }
      }
      if ( selectedCluster ) break;
    }

    if ( !selectedCluster || selectedCluster.length === 0 ) return null;

    selectedCluster = selectedCluster.filter((page) => {
      if ( page.documentId === documentId && page.pageNum === pageNum ) return false;
      return true;
    });

    let pageDuplicate = null;

    for ( let page of selectedCluster ){
      const pageId = generatePageId(page.documentId, page.pageNum);
      if ( pagesIds.includes(pageId) ){
        pageDuplicate = page;
        break;
      }
    }
    return pageDuplicate;
  }
);


// ToDO: In future should be removed with .sort()
const generateMainStaplePageNum = (page:IPage, _:number, array:Array<IPage>) => {
  if ( page.staple ){
    const mainStaplePage:IPage|undefined = array.find((p:IPage) => {
      return p.staple && page.staple && p.staple.order === 1 && p.staple.id === page.staple.id
    });
    if ( mainStaplePage ){
      return { ...page,
        staple: {
          ...page.staple,
          mainStaplePageNum: mainStaplePage.pageNum
        }
      }
    }
  }
  return page;
};
const sortByMainStaplePageNum = (a:any, b:any) => {
  let aPageNum = a.staple ? a.staple.mainStaplePageNum : a.pageNum;
  let bPageNum = b.staple ? b.staple.mainStaplePageNum : b.pageNum;
  if ( a.staple && b.staple && a.staple.id === b.staple.id ){
    aPageNum = a.staple.order;
    bPageNum = b.staple.order;
  }
  return aPageNum - bPageNum;
}
